import { FormEvent, useCallback, useEffect, useMemo, useState } from 'react';
import {
  confirmInspectionSheet,
  createInspectionSheet,
  fetchInspectionSheet,
  fetchInspectionSheetYears,
  reloadInspectionSheetLines,
  saveInspectionSheet,
  downloadInspectionSheetPdf,
} from '../../api/manufacturingEquipment';
import ApprovalCancelButton from '../approval/ApprovalCancelButton';
import ApprovalRequestModal from '../approval/ApprovalRequestModal';
import { APPROVAL_TARGET_INSPECTION_SHEET } from '../../types/approval';
import {
  EquipmentInspectionSheet,
  InspectionSheetAction,
  InspectionSheetDateCell,
  InspectionSheetLine,
  InspectionSheetMark,
} from '../../types/manufacturingEquipment';
import { canConfirmInspection } from '../../types/companyRole';
import { useCompanyStore } from '../../store/companyStore';
import './InspectionSheet.css';

import { notifyError, reportError } from '../../utils/notify';
interface Props {
  equipmentId: number;
}

const MARK_OPTIONS = ['', '/', '△', 'X'] as const;
const H1_MONTHS = ['1월', '2월', '3월', '4월', '5월', '6월'];
const H2_MONTHS = ['7월', '8월', '9월', '10월', '11월', '12월'];

function markKey(lineId: number, halfNo: number, monthIndex: number, weekNo: number) {
  return `${lineId}:${halfNo}:${monthIndex}:${weekNo}`;
}

function dateKey(halfNo: number, monthIndex: number, weekNo: number) {
  return `${halfNo}:${monthIndex}:${weekNo}`;
}

function HalfGrid({
  halfNo,
  months,
  lines,
  markMap,
  dateMap,
  editing,
  onMarkChange,
  onDateChange,
}: {
  halfNo: number;
  months: string[];
  lines: InspectionSheetLine[];
  markMap: Record<string, string>;
  dateMap: Record<string, string>;
  editing: boolean;
  onMarkChange: (lineId: number, halfNo: number, monthIndex: number, weekNo: number, value: string) => void;
  onDateChange: (halfNo: number, monthIndex: number, weekNo: number, value: string) => void;
}) {
  return (
    <div className="insp-sheet__half">
      <table className="insp-sheet__table">
        <thead>
          <tr>
            <th rowSpan={2} className="is-sticky is-sticky-0">
              No
            </th>
            <th rowSpan={2} className="is-sticky is-sticky-1">
              점검개소
            </th>
            <th rowSpan={2} className="is-sticky is-sticky-2">
              점검항목
            </th>
            <th rowSpan={2} className="is-sticky is-sticky-3">
              주기
            </th>
            {months.map((month) => (
              <th key={month} colSpan={5}>
                {month}
              </th>
            ))}
          </tr>
          <tr>
            {months.map((month) =>
              [1, 2, 3, 4, 5].map((week) => (
                <th key={`${month}-${week}`}>{week}</th>
              ))
            )}
          </tr>
        </thead>
        <tbody>
          {lines.length === 0 ? (
            <tr>
              <td colSpan={4 + months.length * 5} className="is-left">
                점검 항목이 없습니다. 관리 기준서에 설비점검기준을 등록한 뒤 ‘기준서에서 불러오기’를 눌러 주세요.
              </td>
            </tr>
          ) : (
            lines.map((line, index) => (
              <tr key={line.lineId}>
                <td className="is-sticky is-sticky-0">{index + 1}</td>
                <td className="is-left is-sticky is-sticky-1">{line.inspectionPoint || '-'}</td>
                <td className="is-left is-sticky is-sticky-2">{line.inspectionItem || '-'}</td>
                <td className="is-sticky is-sticky-3">{line.cycleCode || '-'}</td>
                {months.map((_, monthIndex) =>
                  [1, 2, 3, 4, 5].map((weekNo) => {
                    const key = markKey(line.lineId, halfNo, monthIndex, weekNo);
                    return (
                      <td key={key}>
                        <select
                          value={markMap[key] ?? ''}
                          disabled={!editing}
                          onChange={(e) =>
                            onMarkChange(line.lineId, halfNo, monthIndex, weekNo, e.target.value)
                          }
                        >
                          {MARK_OPTIONS.map((opt) => (
                            <option key={opt || 'empty'} value={opt}>
                              {opt || '·'}
                            </option>
                          ))}
                        </select>
                      </td>
                    );
                  })
                )}
              </tr>
            ))
          )}
          <tr>
            <td colSpan={4} className="is-sticky is-sticky-span">
              점검일자
            </td>
            {months.map((_, monthIndex) =>
              [1, 2, 3, 4, 5].map((weekNo) => {
                const key = dateKey(halfNo, monthIndex, weekNo);
                return (
                  <td key={key}>
                    <input
                      type="date"
                      value={dateMap[key] ?? ''}
                      disabled={!editing}
                      onChange={(e) => onDateChange(halfNo, monthIndex, weekNo, e.target.value)}
                    />
                  </td>
                );
              })
            )}
          </tr>
        </tbody>
      </table>
    </div>
  );
}

function InspectionSheetTab({ equipmentId }: Props) {
  const currentYear = new Date().getFullYear();
  const role = useCompanyStore((state) => state.role);
  const canConfirm = canConfirmInspection(role);
  const [years, setYears] = useState<number[]>([]);
  const [selectedYear, setSelectedYear] = useState(currentYear);
  const [createYear, setCreateYear] = useState(currentYear);
  const [sheet, setSheet] = useState<EquipmentInspectionSheet | null>(null);
  const [missing, setMissing] = useState(false);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [approvalOpen, setApprovalOpen] = useState(false);
  const [approvalMessage, setApprovalMessage] = useState('');
  const [pdfDownloading, setPdfDownloading] = useState(false);
  const [markMap, setMarkMap] = useState<Record<string, string>>({});
  const [dateMap, setDateMap] = useState<Record<string, string>>({});
  const [actions, setActions] = useState<InspectionSheetAction[]>([]);
  const [halfTab, setHalfTab] = useState<1 | 2>(1);

  const loadYears = useCallback(async () => {
    const list = await fetchInspectionSheetYears(equipmentId);
    setYears(list);
    return list;
  }, [equipmentId]);

  const applySheet = (data: EquipmentInspectionSheet) => {
    setSheet({
      ...data,
      lines: data.lines ?? [],
      marks: data.marks ?? [],
      dateCells: data.dateCells ?? [],
      actions: data.actions ?? [],
    });
    setMissing(false);
    const nextMarks: Record<string, string> = {};
    (data.marks ?? []).forEach((mark) => {
      nextMarks[markKey(mark.lineId, mark.halfNo, mark.monthIndex, mark.weekNo)] =
        mark.markValue || '';
    });
    setMarkMap(nextMarks);
    const nextDates: Record<string, string> = {};
    (data.dateCells ?? []).forEach((cell) => {
      nextDates[dateKey(cell.halfNo, cell.monthIndex, cell.weekNo)] = cell.inspectionDate || '';
    });
    setDateMap(nextDates);
    setActions(data.actions?.length ? data.actions : []);
    setEditing(false);
  };

  const loadSheet = useCallback(
    async (year: number) => {
      try {
        setLoading(true);
        setError('');
        const data = await fetchInspectionSheet(equipmentId, year);
        applySheet(data);
      } catch (err) {
        const status = (err as { response?: { status?: number } })?.response?.status;
        if (status === 404) {
          setSheet(null);
          setMissing(true);
          setMarkMap({});
          setDateMap({});
          setActions([]);
          setEditing(false);
        } else {
          reportError(setError, '점검시트를 불러오지 못했습니다.');
        }
      } finally {
        setLoading(false);
      }
    },
    [equipmentId]
  );

  useEffect(() => {
    void (async () => {
      try {
        const list = await loadYears();
        const initial = list.includes(currentYear)
          ? currentYear
          : list[0] ?? currentYear;
        setSelectedYear(initial);
        setCreateYear(initial);
        await loadSheet(initial);
      } catch {
        reportError(setError, '연도 목록을 불러오지 못했습니다.');
        setLoading(false);
      }
    })();
  }, [loadYears, loadSheet, currentYear]);

  const yearOptions = useMemo(() => {
    const set = new Set<number>([...years, selectedYear, currentYear, currentYear + 1]);
    return Array.from(set).sort((a, b) => b - a);
  }, [years, selectedYear, currentYear]);

  const confirmed = sheet?.status === 'CONFIRMED';
  const approvalPending = Boolean(sheet?.approvalInProgress);
  const locked = Boolean(confirmed || approvalPending);

  const handleCreate = async () => {
    try {
      setSaving(true);
      setError('');
      const created = await createInspectionSheet(equipmentId, createYear);
      const list = await loadYears();
      setSelectedYear(createYear);
      if (!list.includes(createYear)) {
        setYears([...list, createYear].sort((a, b) => b - a));
      }
      applySheet(created);
      setEditing(true);
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '점검시트 생성에 실패했습니다. 관리 기준서 점검항목을 먼저 등록하세요.';
      reportError(setError, message);
    } finally {
      setSaving(false);
    }
  };

  const buildPayload = () => {
    const marks: InspectionSheetMark[] = Object.entries(markMap)
      .filter(([, value]) => value)
      .map(([key, markValue]) => {
        const [lineId, halfNo, monthIndex, weekNo] = key.split(':').map(Number);
        return { lineId, halfNo, monthIndex, weekNo, markValue };
      });
    const dateCells: InspectionSheetDateCell[] = Object.entries(dateMap)
      .filter(([, value]) => value)
      .map(([key, inspectionDate]) => {
        const [halfNo, monthIndex, weekNo] = key.split(':').map(Number);
        return { halfNo, monthIndex, weekNo, inspectionDate };
      });
    return { marks, dateCells, actions };
  };

  const handleSave = async (event: FormEvent) => {
    event.preventDefault();
    if (!sheet || !editing || locked) return;
    try {
      setSaving(true);
      const saved = await saveInspectionSheet(equipmentId, sheet.sheetYear, buildPayload());
      applySheet(saved);
      setError('');
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '저장에 실패했습니다.';
      reportError(setError, message);
    } finally {
      setSaving(false);
    }
  };

  const handleConfirm = async () => {
    if (!sheet || locked) return;
    if (!canConfirm) {
      reportError(setError, '점검시트 확인(승인)은 팀장 이상만 가능합니다.');
      return;
    }
    const ok = window.confirm(
      `${sheet.sheetYear}년 점검시트를 확인(승인)할까요? 확인 후에는 수정할 수 없습니다.`
    );
    if (!ok) return;
    try {
      setSaving(true);
      // save first if editing
      if (editing) {
        await saveInspectionSheet(equipmentId, sheet.sheetYear, buildPayload());
      }
      const confirmed = await confirmInspectionSheet(equipmentId, sheet.sheetYear);
      applySheet(confirmed);
      setError('');
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '확인 처리에 실패했습니다.';
      reportError(setError, message);
    } finally {
      setSaving(false);
    }
  };

  const handleCancelEdit = () => {
    if (sheet) applySheet(sheet);
    setEditing(false);
    setError('');
  };

  const handleDownloadPdf = async () => {
    if (!sheet) return;
    try {
      setPdfDownloading(true);
      const name = sheet.equipmentCode?.trim() || String(equipmentId);
      await downloadInspectionSheetPdf(
        equipmentId,
        sheet.sheetYear,
        `점검시트_${name}_${sheet.sheetYear}.pdf`
      );
    } catch (err) {
      notifyError(
        err instanceof Error
          ? err.message
          : 'PDF 다운로드에 실패했습니다. Gotenberg 실행 여부를 확인해 주세요.'
      );
    } finally {
      setPdfDownloading(false);
    }
  };

  const handleReloadLines = async () => {
    if (!sheet || locked) return;
    const ok = window.confirm(
      '관리 기준서의 설비점검기준으로 행을 다시 불러올까요?\n이미 입력한 점검 기호(/, △, X)는 초기화됩니다.'
    );
    if (!ok) return;
    try {
      setSaving(true);
      setError('');
      const reloaded = await reloadInspectionSheetLines(equipmentId, sheet.sheetYear);
      applySheet(reloaded);
    } catch (err) {
      const message =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        '기준서 불러오기에 실패했습니다.';
      reportError(setError, message);
    } finally {
      setSaving(false);
    }
  };

  const lines = sheet?.lines ?? [];

  return (
    <form className={`insp-sheet${editing ? ' is-editing' : ''}`} onSubmit={handleSave}>
      <div className="insp-sheet__toolbar">
        <div>
          <h3>제조 설비 점검 시트</h3>
          <p>연도별로 누적 저장되며, 과거 연도도 조회할 수 있습니다.</p>
          <div className="insp-sheet__year-row" style={{ marginTop: 8 }}>
            <label>점검년도</label>
            <select
              value={selectedYear}
              onChange={(e) => {
                const year = Number(e.target.value);
                setSelectedYear(year);
                setCreateYear(year);
                void loadSheet(year);
              }}
              disabled={editing}
            >
              {yearOptions.map((year) => (
                <option key={year} value={year}>
                  {year}년{years.includes(year) ? '' : ' (미작성)'}
                </option>
              ))}
            </select>
            {!years.includes(createYear) && (
              <>
                <label>새 연도</label>
                <input
                  type="number"
                  min={2000}
                  max={2100}
                  value={createYear}
                  disabled={editing}
                  onChange={(e) => setCreateYear(Number(e.target.value))}
                />
              </>
            )}
          </div>
        </div>
        <div className="insp-sheet__actions">
          {sheet && confirmed ? (
            <button
              type="button"
              disabled={pdfDownloading}
              onClick={() => void handleDownloadPdf()}
            >
              {pdfDownloading ? 'PDF 생성 중…' : 'PDF 다운로드'}
            </button>
          ) : null}
          {sheet && !locked && (
            <button type="button" className="is-ghost" onClick={() => void handleReloadLines()} disabled={saving || editing}>
              기준서에서 불러오기
            </button>
          )}
          {sheet && !locked && !editing && (
            <button type="button" onClick={() => setEditing(true)}>
              수정
            </button>
          )}
          {sheet && editing && (
            <>
              <button type="button" className="is-ghost" onClick={handleCancelEdit} disabled={saving}>
                취소
              </button>
              <button type="submit" disabled={saving}>
                {saving ? '저장 중...' : '저장'}
              </button>
            </>
          )}
          {approvalPending ? (
            <p className="insp-sheet__hint" style={{ margin: 0 }}>
              결재가 진행 중입니다. 내용을 바꾸려면 결재 요청을 취소한 뒤 수정하고 다시 요청하세요.
            </p>
          ) : null}
          <ApprovalCancelButton
            detail={sheet}
            className="is-ghost"
            disabled={saving}
            onCancelled={async () => {
              if (!sheet) return;
              const refreshed = await fetchInspectionSheet(equipmentId, sheet.sheetYear);
              applySheet(refreshed);
              setApprovalMessage('');
            }}
          />
          {sheet && !locked && !editing && (
            <button
              type="button"
              disabled={!sheet.sheetId || saving}
              onClick={() => setApprovalOpen(true)}
            >
              결재 요청
            </button>
          )}
          {sheet && !locked && canConfirm && !editing && (
            <button type="button" className="is-confirm" onClick={() => void handleConfirm()} disabled={saving}>
              확인(승인)
            </button>
          )}
        </div>
      </div>

      {error && <p className="insp-sheet__error">{error}</p>}
      {approvalMessage && <p className="insp-sheet__hint">{approvalMessage}</p>}

      {loading ? (
        <p className="insp-sheet__hint">불러오는 중...</p>
      ) : missing || !sheet ? (
        <div className="insp-sheet__empty">
          <p className="insp-sheet__hint">
            {createYear}년 점검시트가 없습니다. 관리 기준서의 설비점검기준을 불러와 생성합니다.
          </p>
          <button type="button" onClick={() => void handleCreate()} disabled={saving}>
            {saving ? '생성 중...' : `${createYear}년 점검시트 작성`}
          </button>
        </div>
      ) : (
        <>
          <div className="insp-sheet__meta">
            <span>설비명: {sheet.equipmentName || '-'}</span>
            <span>설비번호: {sheet.equipmentCode || '-'}</span>
            <span>점검년도: {sheet.sheetYear}</span>
            <span>상태: {approvalPending ? '결재 요청 중' : sheet.status === 'CONFIRMED' ? '확인완료' : '작성중'}</span>
            <span>작성: {sheet.createdUserName || '-'}</span>
            <span>최종수정: {sheet.updatedUserName || '-'}</span>
            <span>승인: {sheet.confirmedUserName || '-'}</span>
          </div>
          <div className="insp-sheet__legend">
            범례 — / 양호(급유 불필요) · △ 조정(급유) · X 수리·교체 / 주기 D일 W주 M월 Y년
            <br />
            점검결과 이상(X) 발생 시 하단 조치 내역을 반드시 기록하세요.
          </div>

          <div className="insp-sheet__half-tabs" role="tablist" aria-label="점검 반기">
            <button
              type="button"
              role="tab"
              className={`insp-sheet__half-tab${halfTab === 1 ? ' is-active' : ''}`}
              aria-selected={halfTab === 1}
              onClick={() => setHalfTab(1)}
            >
              상반기 (1~6월)
            </button>
            <button
              type="button"
              role="tab"
              className={`insp-sheet__half-tab${halfTab === 2 ? ' is-active' : ''}`}
              aria-selected={halfTab === 2}
              onClick={() => setHalfTab(2)}
            >
              하반기 (7~12월)
            </button>
          </div>

          <HalfGrid
            halfNo={halfTab}
            months={halfTab === 1 ? H1_MONTHS : H2_MONTHS}
            lines={lines}
            markMap={markMap}
            dateMap={dateMap}
            editing={editing && !locked}
            onMarkChange={(lineId, halfNo, monthIndex, weekNo, value) =>
              setMarkMap((prev) => ({
                ...prev,
                [markKey(lineId, halfNo, monthIndex, weekNo)]: value,
              }))
            }
            onDateChange={(halfNo, monthIndex, weekNo, value) =>
              setDateMap((prev) => ({
                ...prev,
                [dateKey(halfNo, monthIndex, weekNo)]: value,
              }))
            }
          />

          <div>
            <div className="insp-sheet__section-head">
              <h4>조정·수리 및 교환 후 내용</h4>
              {editing && !locked && (
                <button
                  type="button"
                  onClick={() =>
                    setActions((prev) => [
                      ...prev,
                      { actionDate: '', abnormality: '', repairDetail: '', remark: '' },
                    ])
                  }
                >
                  행 추가
                </button>
              )}
            </div>
            <table className="insp-sheet__action-table">
              <thead>
                <tr>
                  <th style={{ width: '130px' }}>일자</th>
                  <th>이상 내역</th>
                  <th>수리내용</th>
                  <th>비고</th>
                  {editing && !locked && <th style={{ width: '56px' }} />}
                </tr>
              </thead>
              <tbody>
                {(actions.length ? actions : [{ actionDate: '', abnormality: '', repairDetail: '', remark: '' }]).map(
                  (row, index) => (
                    <tr key={`action-${index}`}>
                      <td>
                        <input
                          type="date"
                          value={row.actionDate ?? ''}
                          disabled={!editing || locked}
                          onChange={(e) =>
                            setActions((prev) => {
                              const base = prev.length ? prev : [{ ...row }];
                              return base.map((item, i) =>
                                i === index ? { ...item, actionDate: e.target.value } : item
                              );
                            })
                          }
                        />
                      </td>
                      <td>
                        <input
                          value={row.abnormality ?? ''}
                          disabled={!editing || locked}
                          onChange={(e) =>
                            setActions((prev) => {
                              const base = prev.length ? prev : [{ ...row }];
                              return base.map((item, i) =>
                                i === index ? { ...item, abnormality: e.target.value } : item
                              );
                            })
                          }
                        />
                      </td>
                      <td>
                        <input
                          value={row.repairDetail ?? ''}
                          disabled={!editing || locked}
                          onChange={(e) =>
                            setActions((prev) => {
                              const base = prev.length ? prev : [{ ...row }];
                              return base.map((item, i) =>
                                i === index ? { ...item, repairDetail: e.target.value } : item
                              );
                            })
                          }
                        />
                      </td>
                      <td>
                        <input
                          value={row.remark ?? ''}
                          disabled={!editing || locked}
                          onChange={(e) =>
                            setActions((prev) => {
                              const base = prev.length ? prev : [{ ...row }];
                              return base.map((item, i) =>
                                i === index ? { ...item, remark: e.target.value } : item
                              );
                            })
                          }
                        />
                      </td>
                      {editing && !locked && (
                        <td className="is-actions">
                          <button
                            type="button"
                            onClick={() =>
                              setActions((prev) => prev.filter((_, i) => i !== index))
                            }
                          >
                            삭제
                          </button>
                        </td>
                      )}
                    </tr>
                  )
                )}
              </tbody>
            </table>
            <p className="insp-sheet__note">
              점검결과 이상 발생 시 즉시 설비관리 책임자에게 보고하고 지시에 따릅니다.
            </p>
          </div>
        </>
      )}

      {sheet?.sheetId ? (
        <ApprovalRequestModal
          open={approvalOpen}
          titleDefault={`${sheet.equipmentName || '설비'} ${sheet.sheetYear}년 점검시트 결재`}
          targetType={APPROVAL_TARGET_INSPECTION_SHEET}
          targetId={sheet.sheetId}
          onClose={() => setApprovalOpen(false)}
          onSubmitted={() => {
            setApprovalOpen(false);
            setApprovalMessage('결재 요청이 등록되었습니다. 헤더의 결재 탭에서 확인할 수 있습니다.');
            if (sheet) {
              void fetchInspectionSheet(equipmentId, sheet.sheetYear).then(applySheet);
            }
          }}
        />
      ) : null}
    </form>
  );
}

export default InspectionSheetTab;
